import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Radio, Monitor, Plug, Clock, CheckCircle2, Wifi, WifiOff } from "lucide-react";
import { sr } from "../../lib/sr";

export default function OpsBoard({ kind }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const p = kind === "radio" ? sr.opsRadio() : sr.opsDigital();
    p.then(setData).catch((e) => setData({ error: e?.response?.data?.detail || true }));
  }, [kind]);

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">{typeof data.error === "string" ? data.error : "Could not load this workstream."}</p></div>;

  const isRadio = kind === "radio";
  const Icon = isRadio ? Radio : Monitor;

  return (
    <div data-testid={`ops-${kind}-page`}>
      <div style={{ marginBottom: 16 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><Icon size={12} /> {isRadio ? "Radio" : "Digital"} operations</span>
        <h1 style={{ fontSize: 30 }}>{isRadio ? "Radio delivery workstream" : "Digital delivery workstream"}</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>Every delivery item for this discipline, plus each provider status.</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12, marginBottom: 20 }} data-testid="providers-row">
        {data.providers.map((p) => (
          <div key={p.key} className="sr-card" data-testid={`provider-${p.key}`}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Plug size={16} style={{ color: "var(--blue)" }} />
                <strong style={{ color: "var(--navy)" }}>{p.name}</strong>
              </div>
              {p.mode === "api" && p.connected
                ? <span className="sr-badge sr-badge-green"><Wifi size={11} /> Connected (API)</span>
                : <span className="sr-badge sr-badge-gray"><WifiOff size={11} /> Managed</span>}
            </div>
            <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>{p.note}</p>
          </div>
        ))}
      </div>

      {isRadio && (
        <div className="sr-card" style={{ marginBottom: 18 }} data-testid="flights-card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <Radio size={16} style={{ color: "var(--blue)" }} />
            <strong style={{ color: "var(--navy)" }}>Flights</strong>
            <span className="sr-badge sr-badge-gray">{data.flights.length}</span>
          </div>
          {data.flights.length === 0 ? <p className="sr-muted" style={{ fontSize: 13 }}>No radio flights booked yet.</p> : (
            <div style={{ display: "grid", gap: 8 }}>
              {data.flights.map((f) => (
                <div key={f.id} className="sr-card" data-testid={`flight-${f.id}`} style={{ background: "#fff", display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                  <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: "var(--navy)", fontWeight: 600 }}>{f.station_name} · {f.daypart}</div>
                    <div className="sr-muted" style={{ fontSize: 12 }}>{f.client_name} · {f.spots_per_week} spots/wk × {f.weeks}wk</div>
                  </div>
                  <span className={`sr-badge ${f.status === "SYNCED" ? "sr-badge-green" : "sr-badge-gray"}`}>{f.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="sr-card" data-testid="ops-tasks-card">
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <Clock size={16} style={{ color: "var(--blue)" }} />
          <strong style={{ color: "var(--navy)" }}>Open work</strong>
          <span className="sr-badge sr-badge-amber">{isRadio ? data.counts.open_tasks : data.counts.open} open</span>
        </div>
        {data.tasks.length === 0 ? <p className="sr-muted" style={{ fontSize: 13 }}>Nothing open in this workstream.</p> : (
          <div style={{ display: "grid", gap: 8 }}>
            {data.tasks.map((t) => (
              <div key={t.id} className="sr-card" data-testid={`ops-task-${t.id}`} style={{ background: "#fff", display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                {t.status === "done" ? <CheckCircle2 size={16} style={{ color: "#067647", flexShrink: 0 }} /> : <Clock size={16} style={{ color: "var(--blue)", flexShrink: 0 }} />}
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: "var(--navy)", fontWeight: 600 }}>{t.title}</div>
                  <div className="sr-muted" style={{ fontSize: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <span>{t.client_name}</span>
                    <span className="sr-badge sr-badge-gray" style={{ fontSize: 10 }}>{t.required_cap}</span>
                    {t.holder_name ? <span>· {t.holder_name}</span> : <span>· unclaimed</span>}
                  </div>
                </div>
                <Link to={`/app/campaigns/${t.campaign_id}/project`} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`ops-open-${t.id}`}>Open</Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
